<script setup lang="ts">
/**
 * Impressum (Anbieterkennzeichnung nach § 5 DDG) als eigene Seite statt Link
 * auf eine fremde Domain. Anbieterdaten aus dem bestehenden Impressum des
 * Betreibers übernommen; Haftungs- und Urheberrechtshinweise auf DDG/DSA
 * aktualisiert, dazu DSA-Kontaktstelle und Meldeweg für rechtswidrige Inhalte.
 * Bewusst noindex: die Seite enthält eine Postanschrift.
 */
const { public: { siteUrl } } = useRuntimeConfig()

useSeoMeta({
  title: 'Legal notice | Clarify',
  description: 'Provider information for Clarify, the ticket bot for Discord: address, contact, DSA contact point and how to report illegal content.',
  robots: 'noindex, follow'
})
useHead({ link: [{ rel: 'canonical', href: `${siteUrl}/imprint` }] })

/* Anbieter ist Moritz Bergmann als Einzelperson (keine Gesellschaft, daher
   keine Vertretungsberechtigten und kein Registereintrag). Eine USt-IdNr.
   gehört hierher, sobald es eine gibt (§ 5 Abs. 1 Nr. 6 DDG). */
const provider = {
  name: 'Moritz Bergmann',
  street: 'Finkeneck 21',
  city: '25421 Pinneberg',
  country: 'Germany',
  phone: '+49 160 97778981',
  phoneHref: 'tel:+4916097778981'
}

// Sichtbar mit [at] gegen simple Adress-Sammler, der Link selbst ist korrekt
const displayedEmail = CONTACT_EMAIL.replace('@', '[at]')
// DSA-Kontaktstelle = Rechtsadresse, wie in Nutzungsbedingungen und Datenschutzerklärung
const displayedReportEmail = REPORT_EMAIL.replace('@', '[at]')
const LAST_UPDATED = 'October 24, 2026'
</script>

<template>
  <main id="main" class="legal">
    <div class="section-shell legal-head shell-gutter">
      <span class="eyebrow">Impressum</span>
      <h1 class="legal-title">Legal notice</h1>
      <p class="copy-muted legal-lead">
        Who runs Clarify, how to reach us, and how to report content that breaks the law.
      </p>
    </div>

    <div class="legal-body">
      <span class="section-rule" aria-hidden="true" />
      <div class="section-shell legal-grid">
        <aside class="legal-aside">
          <div class="card-shell legal-card">
            <span class="cell-plus cell-plus-tl" aria-hidden="true" />
            <span class="cell-plus cell-plus-br" aria-hidden="true" />
            <span class="legal-card-label">Provider · § 5 DDG</span>
            <address class="legal-address">
              <strong>{{ provider.name }}</strong><br>
              {{ provider.street }}<br>
              {{ provider.city }}<br>
              {{ provider.country }}
            </address>
            <dl class="legal-contact">
              <div class="legal-contact-row">
                <dt>Phone</dt>
                <dd><a :href="provider.phoneHref" class="nav-link">{{ provider.phone }}</a></dd>
              </div>
              <div class="legal-contact-row">
                <dt>Email</dt>
                <dd><a :href="`mailto:${CONTACT_EMAIL}`" class="nav-link">{{ displayedEmail }}</a></dd>
              </div>
            </dl>
          </div>
          <UButton
            :to="REPORT_MAILTO"
            label="Report content"
            icon="i-lucide-flag"
            color="neutral"
            variant="outline"
            size="lg"
            block
            class="legal-report"
          />
        </aside>

        <article class="legal-sections">
          <section class="legal-section rule-dash">
            <h2>Responsible for content</h2>
            <p>
              Responsible for the content of this website pursuant to § 18 (2) MStV:
              {{ provider.name }}, address as above.
            </p>
          </section>

          <section class="legal-section rule-dash">
            <h2>Contact point under the DSA</h2>
            <p>
              Our single point of contact for authorities, users and everyone else under
              Articles 11 and 12 of the Digital Services Act (Regulation (EU) 2022/2065) is
              <a :href="`mailto:${REPORT_EMAIL}`" class="nav-link legal-link">{{ displayedReportEmail }}</a>.
              You can write to us in German or English.
            </p>
          </section>

          <section id="report" class="legal-section rule-dash">
            <h2>Report illegal content</h2>
            <p>
              Clarify relays messages between members and server teams on Discord and keeps
              transcripts of closed tickets. If you find content in a ticket, a transcript or
              anywhere else in Clarify that you believe is illegal,
              <a :href="REPORT_MAILTO" class="nav-link legal-link">report it to us by email</a>.
              Please tell us where the content is (message link, server ID or transcript URL),
              why you believe it is illegal, and your name and email address. We review every
              report and act on it without undue delay.
            </p>
          </section>

          <section class="legal-section rule-dash">
            <h2>Consumer dispute resolution</h2>
            <p>
              We are neither willing nor obliged to take part in dispute resolution proceedings
              before a consumer arbitration board (§ 36 VSBG).
            </p>
          </section>

          <section class="legal-section rule-dash">
            <h2>Liability for content</h2>
            <p>
              As a service provider we are responsible for our own content on these pages under
              the general laws. Messages, attachments and form answers that members and server
              teams send through Clarify are third-party content. We transmit and store them for
              the server they belong to, and we are not obliged to monitor this information or to
              investigate circumstances that indicate illegal activity (Articles 4 to 8 DSA). As
              soon as we become aware of specific illegal content, we remove it or disable access
              to it without undue delay.
            </p>
          </section>

          <section class="legal-section rule-dash">
            <h2>Liability for links</h2>
            <p>
              This website links to external websites of third parties whose content we cannot
              influence. The respective provider or operator is always responsible for the content
              of linked pages. We checked linked pages for possible legal violations when we linked
              them and found none. Permanent monitoring of linked pages is not reasonable without
              concrete indications of a violation; if we learn of one, we remove the link without delay.
            </p>
          </section>

          <section class="legal-section rule-dash">
            <h2>Copyright and trademarks</h2>
            <p>
              The texts, graphics and code on this website are protected by German copyright law.
              Any use beyond the limits of copyright law requires our written consent.
            </p>
            <p>
              Discord is a trademark of Discord Inc. Clarify is an independent product and is not
              affiliated with, endorsed or sponsored by Discord. Stripe, PayPal, Tebex, Patreon and
              GitHub are trademarks of their respective owners.
            </p>
          </section>

          <p class="legal-updated">Last updated: {{ LAST_UPDATED }}</p>
        </article>
      </div>
    </div>
  </main>
</template>

<style scoped>
.legal {
  padding-top: clamp(150px, 17vh, 190px);
}

.legal-head {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  padding-bottom: clamp(56px, 7vw, 88px);
}

.legal-title {
  font-size: clamp(2.4rem, 4.4vw, 3.6rem);
  font-weight: 700;
  line-height: 1.04;
  letter-spacing: -0.03em;
  color: var(--nb-ink);
}

.legal-lead {
  max-width: 52ch;
  font-size: 1.06rem;
}

.legal-body {
  position: relative;
  padding: clamp(48px, 6vw, 80px) 0 24px;
}

.legal-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 48px;
}

/* ── Anbieter-Karte ──────────────────────────────────────────────────────── */
.legal-aside {
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding-inline: var(--nb-gutter);
}

.legal-card {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 18px;
  padding: 26px 24px;
  background:
    radial-gradient(120% 120% at 100% 0%, var(--nb-accent-tint), transparent 60%),
    var(--nb-surface);
}

.legal-card-label {
  font-family: var(--font-mono);
  font-size: 0.7rem;
  font-weight: 500;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--nb-ink-faint);
}

.legal-address {
  font-style: normal;
  font-size: 1.02rem;
  line-height: 1.6;
  color: var(--nb-ink-muted);
}

.legal-address strong {
  font-weight: 600;
  color: var(--nb-ink);
}

.legal-contact {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-top: 16px;
  border-top: 1px solid var(--nb-hairline);
}

.legal-contact-row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 16px;
  font-size: 0.94rem;
}

.legal-contact-row dt {
  color: var(--nb-ink-faint);
}

.legal-contact-row dd {
  color: var(--nb-ink);
}

.legal-report {
  border-radius: var(--radius-pill);
  justify-content: center;
}

/* ── Abschnitte: Hairline-Gruppe mit Akzent-Dash (wie die Feature-Zeilen) ─ */
.legal-sections {
  display: flex;
  flex-direction: column;
  gap: 34px;
  min-width: 0;
  padding-inline: var(--nb-gutter);
}

.legal-section {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-top: 26px;
  border-top: 1px solid var(--nb-hairline);
}

.legal-section h2 {
  font-size: 1.12rem;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: var(--nb-ink);
}

.legal-section p {
  max-width: 70ch;
  font-size: 0.98rem;
  line-height: 1.7;
  color: var(--nb-ink-muted);
}

.legal-link {
  color: var(--nb-ink);
  background-size: 100% 1px;
}

.legal-updated {
  font-family: var(--font-mono);
  font-size: 0.72rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--nb-ink-faint);
}

@media (min-width: 900px) {
  .legal-grid {
    grid-template-columns: minmax(300px, 380px) 1fr;
    gap: clamp(40px, 5vw, 80px);
  }

  /* Die Anbieter-Karte bleibt beim Lesen im Blick */
  .legal-aside {
    position: sticky;
    top: 110px;
    align-self: start;
  }
}
</style>
